import { REQUIRED_MESSAGE } from '@wyz/utils';
import { isNil } from 'lodash-es';
import { z } from 'zod';
import { TranslationKeys } from '~/common/types';
import { IDiameter } from '~/purchase/types';

const INVALID_TO_VALUE =
  'app.quote.pricing_policy.errors.to_gt_from' satisfies keyof TranslationKeys;
export const MISSING_DIAMETER =
  'app.quote.pricing_policy.errors.missing_diameter' satisfies keyof TranslationKeys;
export const OVERLAP_DIAMETER =
  'app.quote.pricing_policy.errors.overlap_diameter' satisfies keyof TranslationKeys;
// persisted state
export const PRICING_POLICY_STATUS = {
  ENABLED: 'ENABLED',
  DISABLED: 'DISABLED',
  DRAFT: 'DRAFT',
} as const;

export const PP_CUSTOM_CONSTRAINT = {
  NAME_MAX_LENGTH: 32,
  BLOC_ITEM_NAME_MAX_LENGTH: 56,
  SERVICE_CODE_LENGTH: 13,
  SERVICE_DESCRIPTION_LENGTH: 64,
};

export const DIAMETERS = [
  0.0, 8.0, 10.0, 12.0, 13.0, 14.0, 15.0, 16.0, 17.0, 17.5, 18.0, 19.0, 19.5,
  20.0, 20.5, 21.0, 22.0, 22.5, 23.0, 24.0, 26.0,
];

export const DIAMETER_OPERATORS = {
  LOWER_THAN_OR_EQUAL: 'lte',
  GREATER_THAN_OR_EQUAL: 'gte',
  EQUAL: 'eq',
  BETWEEN: 'between',
} as const;

export const diameterSchema = z
  .object({
    operator: z.enum(DIAMETER_OPERATORS, {
      error: (issue) =>
        issue.input === undefined ? REQUIRED_MESSAGE : REQUIRED_MESSAGE,
    }),
    from: z.number().nullish(),
    to: z.number().nullish(),
  })
  .superRefine((data, ctx) => {
    if (data.operator === DIAMETER_OPERATORS.BETWEEN) {
      if (isNil(data.from) || isNil(data.to)) {
        ctx.addIssue({
          code: 'custom',
          message: REQUIRED_MESSAGE,
          path: ['to'],
          fatal: true,
        });
        return;
      }
      if (data.from > data.to) {
        ctx.addIssue({
          code: 'custom',
          message: INVALID_TO_VALUE,
          path: ['to'],
          fatal: true,
        });
      }
    } else if (data.operator === DIAMETER_OPERATORS.LOWER_THAN_OR_EQUAL) {
      if (isNil(data.to)) {
        ctx.addIssue({
          code: 'custom',
          message: REQUIRED_MESSAGE,
          path: ['to'],
        });
      }
    } else {
      if (isNil(data.from)) {
        ctx.addIssue({
          code: 'custom',
          message: REQUIRED_MESSAGE,
          path: ['from'],
        });
      }
    }
  })
  .nullish();

const resolvedMinDiameter = (diameter: IDiameter): number => {
  if (diameter.operator === DIAMETER_OPERATORS.LOWER_THAN_OR_EQUAL) {
    return Math.min(...DIAMETERS);
  }
  if (isNil(diameter.from)) {
    throw new Error('from is required');
  }
  return diameter.from;
};

const resolvedMaxDiameter = (diameter: IDiameter): number => {
  if (diameter.operator === DIAMETER_OPERATORS.GREATER_THAN_OR_EQUAL) {
    return Math.max(...DIAMETERS);
  }
  if (diameter.operator === DIAMETER_OPERATORS.EQUAL) {
    if (isNil(diameter.from)) {
      throw new Error('from is required');
    }
    return diameter.from;
  }
  if (isNil(diameter.to)) {
    throw new Error('to is required');
  }
  return diameter.to;
};
export function validateRanges(ranges: Array<IDiameter>) {
  const coveredNumbers = new Set();
  let overlap = false;
  let missing = false;
  let minEntered = Infinity;
  let maxEntered = -Infinity;
  // Add all numbers in the ranges to the set
  ranges.forEach((range) => {
    const from = resolvedMinDiameter(range);
    const to = resolvedMaxDiameter(range);
    if (from < minEntered) {
      minEntered = from;
    }
    if (to > maxEntered) {
      maxEntered = to;
    }
    for (let i = from; i <= to; i++) {
      if (coveredNumbers.has(i)) {
        overlap = true;
      }
      coveredNumbers.add(i);
    }
  });

  // Check if all numbers from 0 to 26 are covered
  for (let i = 0; i <= DIAMETERS.length; i++) {
    const number = DIAMETERS[i];
    if (number < minEntered || number > maxEntered) {
      missing = true;
    }
  }

  return { missing, overlap };
}
